// The pillar taxonomy typed into the Generate Full Report modal: pillar names
// and what each one means.
//
// Optional. Leave it empty and the modal behaves exactly as it always has —
// the report engine falls back to whatever `pillars` map the file ships, or to
// the canonical six. Fill it in and autoconfig classifies every open-text and
// Likert question against THESE pillars, with the definitions telling it where
// one ends and its neighbour begins.
//
// Drafted to localStorage so a taxonomy survives closing the modal by accident.
// One key, not one per insight: the modal is also used for a JSON import, where
// no insight exists yet to key against.

import { getOriginalPillarLibrary, getPillarLibrary } from '../../utils/pillarStorage';

const DRAFT_KEY = 'prompta.pillarsDef.draft';
// The project the modal last started its pillars from. A per-browser
// convenience, like the draft: it only decides what the modal opens with.
const PROJECT_KEY = 'prompta.pillarsDef.project';

// Mirrors views.PILLAR_NAME_MAX_LENGTH (itself mirroring impacts' column bound),
// so an over-long name is caught while typing rather than by a 400.
export const PILLAR_NAME_MAX_LENGTH = 100;

export const emptyRow = () => ({ name: '', definition: '' });

// Rows -> the {name: definition} object the BE stores. Blank names are dropped
// (an unfilled row is not an error) and names are compared case-insensitively
// with the first spelling winning, matching the BE's own tie-break.
export const toPayload = (rows) => {
    const payload = {};
    const seen = new Set();
    (rows || []).forEach((row) => {
        const name = String(row?.name || '').trim();
        if (!name) return;
        const key = name.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        payload[name] = String(row?.definition || '').trim();
    });
    return payload;
};

export const fromPayload = (payload) =>
    Object.entries(payload || {}).map(([name, definition]) => ({
        name: String(name),
        definition: String(definition || ''),
    }));

// Returns { errors: {index: message}, formError: string|null }.
// An ENTIRELY empty set of rows is valid — declaring a taxonomy is opt-in.
export const validate = (rows) => {
    const errors = {};
    const seen = new Map();
    let filled = 0;

    (rows || []).forEach((row, index) => {
        const name = String(row?.name || '').trim();
        const definition = String(row?.definition || '').trim();

        if (!name) {
            if (definition) errors[index] = 'Give this pillar a name.';
            return;
        }
        filled += 1;

        if (name.length > PILLAR_NAME_MAX_LENGTH) {
            errors[index] = `Keep the name under ${PILLAR_NAME_MAX_LENGTH} characters.`;
            return;
        }
        const key = name.toLowerCase();
        if (seen.has(key)) {
            errors[index] = `Duplicates "${seen.get(key)}" — names are compared case-insensitively.`;
            return;
        }
        seen.set(key, name);

        if (!definition) {
            errors[index] = 'Add a definition so autoconfig knows what this pillar covers.';
        }
    });

    return {
        errors,
        // Only a half-filled taxonomy blocks. None at all is a valid choice.
        formError: Object.keys(errors).length ? 'Fix the highlighted pillars first.' : null,
        count: filled,
    };
};

// --- draft storage -----------------------------------------------------------
// Every accessor is guarded: in a private window or with site data blocked,
// localStorage throws on ACCESS, not just on write.

export const loadDraft = () => {
    try {
        const raw = window.localStorage.getItem(DRAFT_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return null;
        return parsed
            .filter((row) => row && typeof row === 'object')
            .map((row) => ({
                name: String(row.name || ''),
                definition: String(row.definition || ''),
            }));
    } catch (error) {
        return null;
    }
};

export const saveDraft = (rows) => {
    try {
        // Rows with no NAMED pillar carry no intent — they are just the blank
        // form, or a row someone added and never filled. Persisting them would
        // shadow the library on the next open, which is exactly the bug this
        // guard exists to prevent.
        if (!Object.keys(toPayload(rows)).length) {
            window.localStorage.removeItem(DRAFT_KEY);
            return true;
        }
        window.localStorage.setItem(DRAFT_KEY, JSON.stringify(rows || []));
        return true;
    } catch (error) {
        return false;
    }
};

export const clearDraft = () => {
    try {
        window.localStorage.removeItem(DRAFT_KEY);
        return true;
    } catch (error) {
        return false;
    }
};

// What the modal opens with, in priority order:
//   1. an unfinished DRAFT — someone was mid-edit and closed the modal
//   2. the user's saved pillar LIBRARY (Manage Users -> Default Pillars)
//   3. a single blank row
//
// The library is the point: most runs use the same taxonomy, so the modal should
// arrive pre-filled rather than making you retype it. A draft still wins, since
// it represents a deliberate in-progress change to that default.
export const initialRows = () => {
    const draft = loadDraft();
    // Same rule on the way in, so a blank draft written by an older build (or
    // by a storage write that failed) still can't shadow the library.
    if (draft && Object.keys(toPayload(draft)).length) return draft;
    const library = getPillarLibrary();
    if (library && library.length) {
        return library.map(({ name, definition }) => ({ name, definition }));
    }
    return [emptyRow()];
};

// True when an unfinished draft would win the next open — the one thing a
// project prefill must not overwrite.
export const hasDraft = () => {
    const draft = loadDraft();
    return Boolean(draft && Object.keys(toPayload(draft)).length);
};

export const loadPillarsProject = () => {
    try {
        return window.localStorage.getItem(PROJECT_KEY) || '';
    } catch (error) {
        return '';
    }
};

export const savePillarsProject = (projectId) => {
    try {
        if (projectId) window.localStorage.setItem(PROJECT_KEY, String(projectId));
        else window.localStorage.removeItem(PROJECT_KEY);
        return true;
    } catch (error) {
        return false;
    }
};

// A project's pillars (GET /insights/projects/<id>/pillars/) -> editor rows.
//
// The canonical six come back with a blank definition, and a named row without
// one blocks generation — so a blank definition is filled from the user's
// library, then from the shipped six, matched on name or key ignoring case
// ("success criteria" is "Success Criteria"). A custom pillar keeps its own
// definition; if it has none the editor asks for one, as it would for any row.
export const rowsFromProjectPillars = (pillars) => {
    const lookup = new Map();
    [...getOriginalPillarLibrary(), ...getPillarLibrary()].forEach(({ name, definition }) => {
        if (definition) lookup.set(String(name).toLowerCase(), definition);
    });
    const rows = (Array.isArray(pillars) ? pillars : []).map((pillar) => {
        const name = String(pillar?.label || pillar?.name || pillar?.key || '').trim();
        const own = String(pillar?.definition || '').trim();
        const definition =
            own ||
            lookup.get(name.toLowerCase()) ||
            lookup.get(String(pillar?.key || '').toLowerCase()) ||
            '';
        return { name, definition };
    }).filter((row) => row.name);
    return rows.length ? rows : [emptyRow()];
};
